import { type ReactNode } from "react";
import { ChevronRightIcon } from "@/components/icons";
import { formatNumber } from "@/utils";
import styles from "./TreeRow.module.css";

type TreeRowBaseProps = {
  label: string;
  // 이름 span 의 title. 이름이 잘렸을 때 전체 값을 보여 준다. 생략하면 label 을 쓴다.
  title?: string;
  depth: number;
  // 이름 앞 글리프. 슬롯의 크기와 색은 이 컴포넌트가 정하고, 글리프 종류는 호출부가 정한다.
  icon: ReactNode;
  // 행 끝의 건수. null 이면 그리지 않는다. 건수의 모집단이 옆 목록과 다를 때 쓴다.
  count: number | null;
  // 행 끝 액션(메뉴, 업로드 버튼). 본문 버튼 바깥의 형제 요소이므로 버튼이 중첩되지 않는다.
  trailing?: ReactNode;
  // 공간 예약과 hover 표시는 함께 켠다. 공간을 예약하지 않고 표시하면 이름이 이동한다.
  revealTrailingOnHover?: boolean;
};

type TreeRowSelectProps = TreeRowBaseProps & {
  mode: "select";
  selected: boolean;
  onSelect: () => void;
  // undefined 이면 캐럿을 그리지 않지만 그 폭은 유지한다.
  expanded?: boolean;
  onToggleExpanded?: () => void;
  // 캐럿 칸을 둘지 정한다. 트리에 펼칠 수 있는 노드가 없으면 빈 칸만 남으므로 호출부가 끈다.
  // **한 트리 안에서는 모든 행이 같은 값을 받아야 한다.** 행마다 다르면 이름 시작 위치가
  // 달라져 같은 계층이 다른 계층처럼 보인다.
  caretColumn?: boolean;
};

type TreeRowExpandProps = TreeRowBaseProps & {
  mode: "expand";
  expanded: boolean;
  onToggleExpanded: () => void;
};

export type TreeRowProps = TreeRowSelectProps | TreeRowExpandProps;

/**
 * 데이터셋 트리 두 곳(폴더 레인, 파일 브라우저)이 공유하는 행.
 *
 * `mode` 는 시각 변형이 아니라 상호작용 모델이다. 그래서 판별 union 이고 기본값이 없다.
 * - `select`: 캐럿(펼침)과 본문(선택)이 형제 버튼이다. 한 버튼으로 합치면 부모를 펼치려는
 *   클릭이 목록도 다시 조회한다.
 * - `expand`: 본문 전체가 펼침 토글이다. 행 전체가 클릭 영역이므로 이름 부분만 눌리지 않는
 *   문제가 없다. 선택 축이 없다.
 *
 * `aria-label` 은 글자 없는 컨트롤에만 둔다. select 모드의 캐럿 버튼 하나다.
 * 본문 버튼은 자기 텍스트(이름 + 건수)가 접근성 이름이고, 상태는 `aria-expanded` 가 알린다.
 *
 * 비목표: `role="tree"`/`treeitem`. 이 role 을 선언하면 ↑↓←→ 키보드 계약도 지켜야 한다.
 * 그런데 파일 브라우저의 ↑↓ 는 이미 에피소드 목록 이동에 쓴다.
 */
export function TreeRow(props: TreeRowProps): ReactNode {
  const { label, title, depth, icon, count, trailing, revealTrailingOnHover = false } = props;
  const selected = props.mode === "select" && props.selected;

  return (
    // 배경은 행이 칠한다. 선택 표시가 클래스가 아니라 속성인 이유는 CSS 주석에 있다.
    <div
      className={styles.row}
      data-selected={selected ? "true" : undefined}
      style={{ paddingLeft: `calc(var(--tree-row-inset) + ${depth} * var(--tree-indent))` }}
    >
      {props.mode === "select" ? <SelectCaret {...props} /> : null}
      <button
        type="button"
        className={styles.body}
        aria-current={selected ? "true" : undefined}
        aria-expanded={props.mode === "expand" ? props.expanded : undefined}
        onClick={props.mode === "select" ? props.onSelect : props.onToggleExpanded}
      >
        {props.mode === "expand" ? (
          <span className={styles.caret} aria-hidden="true">
            <Chevron expanded={props.expanded} />
          </span>
        ) : null}
        <span className={styles.icon} aria-hidden="true">
          {icon}
        </span>
        <span className={styles.name} title={title ?? label}>
          {label}
        </span>
        {count === null ? null : <span className={styles.count}>{formatNumber(count)}</span>}
      </button>
      <span
        className={revealTrailingOnHover ? styles.trailingReveal : styles.trailing}
        aria-hidden={trailing == null}
      >
        {trailing}
      </span>
    </div>
  );
}

function SelectCaret({
  label,
  expanded,
  onToggleExpanded,
  caretColumn = true,
}: TreeRowSelectProps): ReactNode {
  if (onToggleExpanded === undefined) {
    // 펼칠 노드가 있는 트리에서만 빈 캐럿 칸을 유지한다. 펼칠 자식이 있는 행은 칸이 꺼져 있어도
    // 캐럿을 그린다. 캐럿이 없으면 그 행을 펼칠 방법이 없다.
    return caretColumn ? <span className={styles.caretPlaceholder} aria-hidden="true" /> : null;
  }
  return (
    <button
      type="button"
      className={styles.caretButton}
      aria-label={expanded === true ? `${label} 접기` : `${label} 펼치기`}
      aria-expanded={expanded === true}
      onClick={onToggleExpanded}
    >
      <Chevron expanded={expanded === true} />
    </button>
  );
}

function Chevron({ expanded }: { expanded: boolean }): ReactNode {
  return (
    <span className={expanded ? styles.chevronDown : undefined} aria-hidden="true">
      <ChevronRightIcon />
    </span>
  );
}
